import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Container, CircularProgress, Box, Typography, Dialog, DialogTitle, DialogContent, Tabs, Tab, IconButton, Alert, Button, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Header from './components/Header';
import LoginButton from './components/LoginButton';
import SubmitScore from './components/SubmitScore';
import RankingsCarousel from './components/RankingsCarousel';
import BarCarousel from './components/BarCarousel';
import AchievementForm from './components/AchievementForm';
import AchievementCarousel from './components/AchievementCarousel';
import AddBarModal from './components/AddBarModal';
import PhotoCarousel from './components/PhotoCarousel';
import Rules from './components/Rules';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { AuthProvider, useAuth } from './components/AuthContext';
import axios from 'axios';

// Material 3 Dark Theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#667eea',
      light: '#8fa3f5',
      dark: '#4a5bd5',
    },
    secondary: {
      main: '#764ba2',
      light: '#9575cd',
      dark: '#5e3a7a',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          '&::before': {
            display: 'none',
          },
        },
      },
    },
  },
});

// Use relative API URL for same-origin requests
const API_URL = process.env.REACT_APP_API_URL || '';

function AppContent() {
  const [bars, setBars] = useState([]);
  const [currentBar, setCurrentBar] = useState(null); // Server's actual current bar
  const [viewedBar, setViewedBar] = useState(null); // What the user is viewing in carousel
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [contentTab, setContentTab] = useState(0); // 0 = Rankings, 1 = Rules
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [achievementFormOpen, setAchievementFormOpen] = useState(false);
  const [achievementIsTrap, setAchievementIsTrap] = useState(false);
  const [addBarModalOpen, setAddBarModalOpen] = useState(false);
  const [achievementRefreshTrigger, setAchievementRefreshTrigger] = useState(0);
  const [photoRefreshTrigger, setPhotoRefreshTrigger] = useState(0);
  const [userHandicaps, setUserHandicaps] = useState(new Map());
  const [nameToUserId, setNameToUserId] = useState(new Map());
  const [userPhotoCounts, setUserPhotoCounts] = useState(new Map());
  const [rulesContent, setRulesContent] = useState('');
  const [showRulesJumbotron, setShowRulesJumbotron] = useState(false);
  const [userHasReadRules, setUserHasReadRules] = useState(false);
  const { user, login, adminDisplayEnabled, refreshUser } = useAuth();

  const contentTabLabels = ['Rankings', 'Rules'];

  useEffect(() => {
    fetchBars();
    fetchCurrentBar();
    fetchRules();
    // OAuth callback is now handled by AuthContext via URL hash
  }, []);

  // Check if user has read rules and show jumbotron if needed
  useEffect(() => {
    if (user) {
      // Check has_read_rules from user object (fetched from /api/auth/me)
      const hasReadRules = user.has_read_rules || false;
      setUserHasReadRules(hasReadRules);
      if (!hasReadRules) {
        setShowRulesJumbotron(true);
      } else {
        setShowRulesJumbotron(false);
      }
    } else {
      setShowRulesJumbotron(false);
      setUserHasReadRules(false);
    }
  }, [user]);

  const fetchRules = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rules`);
      setRulesContent(response.data.content || '');
    } catch (error) {
      console.error('Error fetching rules:', error);
    }
  };

  const handleRulesRead = async () => {
    try {
      await axios.put(
        `${API_URL}/api/auth/me/rules-read`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      // Refresh user data in AuthContext to get updated has_read_rules value
      await refreshUser();
      setShowRulesJumbotron(false);
    } catch (error) {
      console.error('Error marking rules as read:', error);
      // Still hide the jumbotron on error to avoid blocking the user
      setShowRulesJumbotron(false);
    }
  };

  // Set initial viewed bar when bars are loaded or current bar is fetched
  useEffect(() => {
    if (bars.length > 0) {
      // If we have a current bar, always prefer it as the viewed bar
      if (currentBar) {
        const currentBarExists = bars.find(b => b.id === currentBar.id);
        if (currentBarExists) {
          // Only update if viewedBar is not set or is different from currentBar
          if (!viewedBar || viewedBar.id !== currentBar.id) {
            setViewedBar(currentBar);
          }
        }
      } else if (!viewedBar) {
        // No current bar, set to first bar if viewedBar not set
        setViewedBar(bars[0]);
      }
      
      // If viewed bar was deleted, switch to current bar or first bar
      if (viewedBar && !bars.find(b => b.id === viewedBar.id)) {
        setViewedBar(currentBar || bars[0]);
      }
    }
  }, [bars, currentBar]);

  const fetchBars = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bars`);
      const barsData = response.data;
      // Sort bars by order field (backend should already sort, but ensure it here too)
      const sortedBars = [...barsData].sort((a, b) => (a.order || 0) - (b.order || 0));
      setBars(sortedBars);
      // Fetch handicaps before marking as loaded so rankings show final state immediately
      if (barsData.length > 0 && barsData.some(bar => bar.scores && bar.scores.length > 0)) {
        await fetchUserHandicaps(barsData);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bars:', error);
      setError('Failed to load bars. Please refresh the page.');
      setLoading(false);
    }
  };

  const fetchUserHandicaps = async (barsData) => {
    const handicapsMap = new Map();
    const userIds = new Set();
    const nameToUserIdMap = new Map();
    
    // Collect all user IDs from scores, and track names for lookup
    barsData.forEach((bar) => {
      bar.scores?.forEach((score) => {
        if (score.user_id) {
          userIds.add(score.user_id);
          if (score.name) {
            nameToUserIdMap.set(score.name, score.user_id);
          }
        }
      });
    });

    // For scores without user_id, try to look up by name
    for (const [name, userId] of nameToUserIdMap.entries()) {
      if (userId && !userIds.has(userId)) {
        userIds.add(userId);
      }
    }

    // Also try to look up users by name if we don't have their ID
    const namesToLookup = new Set();
    barsData.forEach((bar) => {
      bar.scores?.forEach((score) => {
        if (!score.user_id && score.name && !nameToUserIdMap.has(score.name)) {
          namesToLookup.add(score.name);
        }
      });
    });

    // Look up user IDs by name
    for (const name of namesToLookup) {
      try {
        const response = await axios.get(`${API_URL}/api/users/by-name/${encodeURIComponent(name)}`);
        if (response.data.id) {
          userIds.add(response.data.id);
          nameToUserIdMap.set(name, response.data.id);
        }
      } catch (error) {
        console.error(`Error looking up user by name ${name}:`, error);
      }
    }
    setNameToUserId(nameToUserIdMap);

    // Track userIds that have scores (before fetching achievements)
    const userIdsWithScores = new Set(userIds);
    
    // Fetch all user achievements in a single request
    try {
      const response = await axios.get(`${API_URL}/api/users/achievements/all`);
      const allAchievements = response.data;
      // Convert the response object to a Map
      Object.entries(allAchievements).forEach(([userId, achievements]) => {
        handicapsMap.set(userId, achievements);
        // Also collect userIds that have achievements (even if they don't have scores)
        userIds.add(userId);
      });
    } catch (error) {
      console.error('Error fetching all user achievements:', error);
    }
    
    // Fetch user info for users with achievements but no scores to get their names
    // This ensures we can include them in rankings even without bar scores
    const userIdsWithAchievements = Array.from(handicapsMap.keys());
    const userIdsWithoutScores = userIdsWithAchievements.filter(userId => !userIdsWithScores.has(userId));
    
    // Fetch user info for users with achievements but no scores
    const userInfoPromises = userIdsWithoutScores.map(async (userId) => {
      try {
        const response = await axios.get(`${API_URL}/api/users/${userId}/info`);
        if (response.data && response.data.name) {
          nameToUserIdMap.set(response.data.name, userId);
        }
        return { userId, name: response.data?.name || null };
      } catch (error) {
        // User might not exist or endpoint might not be available yet
        return { userId, name: null };
      }
    });
    await Promise.all(userInfoPromises);
    
    setNameToUserId(nameToUserIdMap);
    setUserHandicaps(handicapsMap);
    
    // Fetch photo counts for all users in parallel
    const photoCountsMap = new Map();
    const photoPromises = Array.from(userIds).map(async (userId) => {
      try {
        const response = await axios.get(`${API_URL}/api/users/${userId}/photos`);
        return { userId, count: response.data.length };
      } catch (error) {
        console.error(`Error fetching photo count for user ${userId}:`, error);
        return { userId, count: 0 };
      }
    });
    
    const photoResults = await Promise.all(photoPromises);
    photoResults.forEach(({ userId, count }) => {
      photoCountsMap.set(userId, count);
    });
    setUserPhotoCounts(photoCountsMap);
  };

  // Calculate total scores for each player (including handicaps)
  // This will recalculate automatically when handicaps load
  const playerScores = useMemo(() => {
    const playerMap = new Map();

    // Aggregate scores by player name across all bars
    bars.forEach((bar) => {
      bar.scores?.forEach((score) => {
        const playerName = score.name;
        let userId = score.user_id;
        
        // If no user_id in score, try to look it up from our name mapping
        if (!userId && nameToUserId.has(playerName)) {
          userId = nameToUserId.get(playerName);
        }
        
        if (!playerMap.has(playerName)) {
          playerMap.set(playerName, {
            name: playerName,
            avatar: score.avatar,
            id: userId,
            totalScore: 0,
            scores: [],
            handicaps: [],
          });
        }
        const player = playerMap.get(playerName);
        // Update user ID if we found one
        if (userId && !player.id) {
          player.id = userId;
        }
        player.totalScore += score.score;
        player.scores.push({
          barName: bar.name,
          score: score.score,
        });
      });
    });

    // Add players who have achievements (handicaps/traps) but no bar scores
    // This ensures they show up in rankings even without scores
    // Build reverse map: userId -> name
    const userIdToNameMap = new Map();
    nameToUserId.forEach((id, name) => {
      userIdToNameMap.set(id, name);
    });
    
    userHandicaps.forEach((achievements, userId) => {
      if (achievements && achievements.length > 0) {
        // Find the user's name from the reverse map
        let playerName = userIdToNameMap.get(userId);
        
        // If we found a name and they're not already in the map, add them
        if (playerName && !playerMap.has(playerName)) {
          playerMap.set(playerName, {
            name: playerName,
            avatar: null, // Will be set if we find it later
            id: userId,
            totalScore: 0,
            scores: [],
            handicaps: [],
          });
        }
      }
    });

    // Add handicaps and photo counts to player scores
    playerMap.forEach((player) => {
      if (player.id) {
        const allAchievements = userHandicaps.get(player.id) || [];
        // Separate handicaps and traps
        const handicaps = allAchievements.filter(a => !a.is_trap || a.is_trap === false);
        const traps = allAchievements.filter(a => a.is_trap === true);
        player.handicaps = handicaps;
        player.traps = traps;
        // Add handicap par scores to total (handicaps have negative par)
        handicaps.forEach((handicap) => {
          player.totalScore += handicap.par_score;
        });
        // Add trap par scores to total
        traps.forEach((trap) => {
          player.totalScore += trap.par_score;
        });
        // Add photo count
        player.photoCount = userPhotoCounts.get(player.id) || 0;
      } else {
        player.photoCount = 0;
      }
    });

    // Convert to array and sort by total score (ascending - lower is better)
    return Array.from(playerMap.values())
      .sort((a, b) => a.totalScore - b.totalScore);
  }, [bars, userHandicaps, nameToUserId, userPhotoCounts]);

  const fetchCurrentBar = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/current-bar`);
      const serverCurrentBar = response.data;
      setCurrentBar(serverCurrentBar);
    } catch (error) {
      console.error('Error fetching current bar:', error);
      setCurrentBar(null);
    }
  };

  const handleBarChange = (bar) => {
    // Only update the viewed bar, not the server's current bar
      const barId = typeof bar === 'string' ? bar : bar.id;
      const fullBar = bars.find(b => b.id === barId);
      if (fullBar) {
      setViewedBar(fullBar);
    }
  };

  const handleCurrentBarChange = async (newCurrentBar) => {
    // This is called when admin sets/unsets the current bar in EditBarModal
    if (newCurrentBar) {
      setCurrentBar(newCurrentBar);
      // Refresh from server to ensure we have latest data
      await fetchCurrentBar();
    } else {
      setCurrentBar(null);
    }
  };

  const handleBarUpdate = (updatedBar) => {
    setBars(bars.map(b => b.id === updatedBar.id ? updatedBar : b));
    // Update current bar if it was the one edited
    if (currentBar && currentBar.id === updatedBar.id) {
      setCurrentBar(updatedBar);
    }
  };

  const handleBarAdd = (newBar) => {
    setBars([...bars, newBar]);
    // Refresh bars to ensure proper ordering
    fetchBars();
  };

  const handleBarsReorder = async (barOrders) => {
    try {
      await axios.put(
        `${API_URL}/api/bars/order`,
        { bar_orders: barOrders },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      // Refresh bars to get updated order
      fetchBars();
    } catch (error) {
      console.error('Error updating bar order:', error);
      alert('Failed to update bar order. Please try again.');
    }
  };

  const handleBarDelete = async (barId) => {
    try {
      await axios.delete(`${API_URL}/api/bars/${barId}`);
      setBars(bars.filter(b => b.id !== barId));
      // If deleted bar was current bar, fetch new current bar
      if (currentBar && currentBar.id === barId) {
        await fetchCurrentBar();
      }
    } catch (error) {
      console.error('Error deleting bar:', error);
      alert('Failed to delete bar. Please try again.');
    }
  };

  const handleScoreSubmitted = async () => {
    // Refresh bars (which includes sorting) and current bar after score submission
    await fetchBars();
    await fetchCurrentBar();
  };


  const handleLogin = async () => {
    try {
      await login('google');
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  return (
    <div className="app">
      <Header onLoginClick={() => setLoginDialogOpen(true)} showImage={!user} />
      <main className="main-content">
        <Container maxWidth={false} sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 0.5, sm: 1.5, md: 2 }, maxWidth: '900px', mx: 'auto' }}>
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          )}
          {error && (
            <Box textAlign="center" py={4}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}
          {!loading && !error && (
            <>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 2,
                  p: 0,
                  minHeight: '600px',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ p: 2 }}>
                  {/* Rules Jumbotron - Only for logged-in users who haven't read rules */}
                  {user && showRulesJumbotron && (
                    <Box
                      sx={{
                        mb: 3,
                        animation: showRulesJumbotron ? 'slideIn 0.5s ease-out' : 'slideOut 0.5s ease-in',
                        '@keyframes slideIn': {
                          from: {
                            opacity: 0,
                            transform: 'translateY(-20px)',
                          },
                          to: {
                            opacity: 1,
                            transform: 'translateY(0)',
                          },
                        },
                        '@keyframes slideOut': {
                          from: {
                            opacity: 1,
                            transform: 'translateY(0)',
                          },
                          to: {
                            opacity: 0,
                            transform: 'translateY(-20px)',
                            maxHeight: 0,
                            marginBottom: 0,
                            overflow: 'hidden',
                          },
                        },
                      }}
                    >
                      <Paper
                        elevation={8}
                        sx={{
                          p: 4,
                          backgroundColor: 'rgba(102, 126, 234, 0.95)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: 3,
                          color: 'white',
                          border: '2px solid rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            mb: 3,
                            textAlign: 'center',
                            fontWeight: 700,
                            color: 'white',
                          }}
                        >
                          Rules
                        </Typography>
                        <Box
                          sx={{
                            mb: 3,
                            maxHeight: '400px',
                            overflowY: 'auto',
                            '& h1, & h2, & h3': {
                              color: 'white',
                              marginTop: 2,
                              marginBottom: 1,
                            },
                            '& p': {
                              color: 'rgba(255, 255, 255, 0.95)',
                              marginBottom: 1.5,
                              lineHeight: 1.6,
                            },
                            '& ul, & ol': {
                              color: 'rgba(255, 255, 255, 0.95)',
                              marginBottom: 1.5,
                              paddingLeft: 3,
                            },
                            '& li': {
                              marginBottom: 0.5,
                            },
                            '& a': {
                              color: '#fff',
                              textDecoration: 'underline',
                            },
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              background: 'rgba(255, 255, 255, 0.1)',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              background: 'rgba(255, 255, 255, 0.3)',
                              borderRadius: '4px',
                              '&:hover': {
                                background: 'rgba(255, 255, 255, 0.5)',
                              },
                            },
                          }}
                          dangerouslySetInnerHTML={{ __html: rulesContent }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Button
                            variant="contained"
                            size="large"
                            onClick={handleRulesRead}
                            sx={{
                              px: 4,
                              py: 1.5,
                              fontSize: '1.1rem',
                              fontWeight: 600,
                              backgroundColor: 'white',
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              },
                            }}
                          >
                            I've Read The Rules
                          </Button>
                        </Box>
                      </Paper>
                    </Box>
                  )}
                  
                  {/* Bar Carousel - Always visible */}
                  <BarCarousel
                    bars={bars}
                    serverCurrentBar={currentBar}
                    viewedBar={viewedBar}
                    onBarChange={handleBarChange}
                    onUpdate={handleBarUpdate}
                    onDelete={handleBarDelete}
                    onCurrentBarChange={handleCurrentBarChange}
                    onAdd={handleBarAdd}
                    onBarsReorder={handleBarsReorder}
                    onLogScoreClick={() => setScoreDialogOpen(true)}
                  />
                  
                  {currentBar && (
                    <>
                      <SubmitScore
                        currentBar={currentBar}
                        onScoreSubmitted={handleScoreSubmitted}
                        open={scoreDialogOpen}
                        onClose={() => setScoreDialogOpen(false)}
                      />
                    </>
                  )}
                  
                  {/* Handicaps Carousel */}
                  <Box sx={{ mb: 4, mt: 3 }}>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 1, position: 'relative' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, textTransform: 'uppercase', textAlign: 'center' }}>
                        Handicaps
                      </Typography>
                      {adminDisplayEnabled && (
                        <IconButton
                          onClick={() => {
                            setAchievementIsTrap(false);
                            setAchievementFormOpen(true);
                          }}
                      sx={{
                        backgroundColor: '#1e1e1e',
                        color: 'white',
                        fontWeight: 'bold',
                        border: '1px solid',
                        borderColor: 'divider',
                            position: 'absolute',
                            right: 0,
                        '&:hover': {
                          backgroundColor: '#2e2e2e',
                        },
                      }}
                          title="Add Handicap"
                    >
                      <AddIcon sx={{ fontWeight: 'bold' }} />
                    </IconButton>
                  )}
                    </Box>
                    <AchievementCarousel refreshTrigger={achievementRefreshTrigger} isTrap={false} onClaim={handleScoreSubmitted} />
                  </Box>
                  
                  {/* Traps Carousel */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 1, position: 'relative' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, textTransform: 'uppercase', textAlign: 'center' }}>
                        Traps
                      </Typography>
                      {adminDisplayEnabled && (
                    <IconButton
                          onClick={() => {
                            setAchievementIsTrap(true);
                            setAchievementFormOpen(true);
                          }}
                      sx={{
                        backgroundColor: '#1e1e1e',
                        color: 'white',
                        fontWeight: 'bold',
                        border: '1px solid',
                        borderColor: 'divider',
                            position: 'absolute',
                            right: 0,
                        '&:hover': {
                          backgroundColor: '#2e2e2e',
                        },
                      }}
                          title="Add Trap"
                    >
                      <AddIcon sx={{ fontWeight: 'bold' }} />
                    </IconButton>
                  )}
                </Box>
                    <AchievementCarousel refreshTrigger={achievementRefreshTrigger} isTrap={true} onClaim={handleScoreSubmitted} />
                  </Box>
                  
                  {/* Photos Carousel */}
                  <Box sx={{ mb: 4, mt: 4 }}>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 1, position: 'relative' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, textTransform: 'uppercase', textAlign: 'center' }}>
                        Photos
                      </Typography>
                    </Box>
                    <PhotoCarousel refreshTrigger={photoRefreshTrigger} />
                    
                    {/* Add Photo Button */}
                    {user && (
                      <Box sx={{ mt: 3, width: '100%' }}>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="photo-upload-carousel"
                          type="file"
                          onChange={async (event) => {
                            const file = event.target.files[0];
                            if (!file) return;

                            if (!file.type.startsWith('image/')) {
                              alert('Please select an image file');
                              return;
                            }

                            try {
                              const formData = new FormData();
                              formData.append('file', file);

                              await axios.post(`${API_URL}/api/photos`, formData, {
                                headers: {
                                  'Content-Type': 'multipart/form-data',
                                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                },
                              });

                              setPhotoRefreshTrigger(prev => prev + 1);
                            } catch (error) {
                              console.error('Error uploading photo:', error);
                              if (error.response?.status === 401) {
                                alert('Please sign in to upload photos');
                              } else {
                                alert('Failed to upload photo. Please try again.');
                              }
                            } finally {
                              event.target.value = '';
                            }
                          }}
                        />
                        <label htmlFor="photo-upload-carousel" style={{ width: '100%', display: 'block' }}>
                          <Button
                            variant="contained"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                            fullWidth
                            sx={{ py: 1.5 }}
                          >
                            Upload Photo
                          </Button>
                        </label>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Content Tabs (Rankings, Rules) */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, mt: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <Tabs 
                      value={contentTab} 
                      onChange={(e, newValue) => setContentTab(newValue)} 
                      sx={{ flex: 1 }}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                    >
                      {contentTabLabels.map((label, index) => (
                            <Tab key={index} label={label} />
                          ))}
                        </Tabs>
                      </Box>
                  
                  {/* Content */}
                  {contentTab === 0 && (
                    <RankingsCarousel 
                          bars={bars} 
                          onUpdate={handleScoreSubmitted}
                          playerScores={playerScores}
                        />
                      )}
                  {contentTab === 1 && (
                        <Rules />
                  )}
                </Box>
              </Box>
              {user && (
                <Box
                  sx={{
                    width: '100%',
                    margin: '40px auto 0',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: 4,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src="/james_natalie_golf_opt.jpg"
                    alt="James & Natalie Golf"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Container>
      </main>

      <Dialog 
        open={loginDialogOpen} 
        onClose={() => setLoginDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 2 },
            maxWidth: { xs: 'calc(100% - 16px)', sm: '400px' },
            width: '100%',
          },
        }}
      >
        <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>Sign In</DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            minWidth: { xs: 'auto', sm: 300 },
            width: '100%',
            py: { xs: 1, sm: 2 },
          }}>
            <LoginButton onClick={handleLogin} />
          </Box>
        </DialogContent>
      </Dialog>

      <AchievementForm
        open={achievementFormOpen}
        onClose={() => setAchievementFormOpen(false)}
        onSuccess={() => {
          // Refresh achievements list
          setAchievementRefreshTrigger(prev => prev + 1);
        }}
        isTrap={achievementIsTrap}
      />

      <AddBarModal
        open={addBarModalOpen}
        onClose={() => setAddBarModalOpen(false)}
        onAdd={handleBarAdd}
      />

    </div>
  );
}

function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
